import React, { useState, useCallback, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import SourceSection from './SourceSection';
import { sanitizeWebAnswer, sanitizeSources, isGenuineError } from '../utils/responseSanitizer';
import './RBIChatbot.css';
import './RBIChatbot-extra.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const SOURCES = [
  { id: 'rbi', title: 'RBI Research', icon: '\uD83C\uDFDB\uFE0F', accent: '#1e3a8a', endpoint: '/api/policy-answer', loadingMsg: 'Analyzing RBI documents...', emptyMsg: 'RBI regulatory answers will appear here.' },
  { id: 'web', title: 'Web Research', icon: '\uD83C\uDF10', accent: '#059669', endpoint: '/api/web-answer', loadingMsg: 'Searching trusted web sources...', emptyMsg: 'Web search results will appear here.' },
  { id: 'icai', title: 'ICAI Research', icon: '\uD83D\uDCCB', accent: '#7c3aed', endpoint: '/api/data-answer', loadingMsg: 'Reviewing ICAI audit documents...', emptyMsg: 'ICAI audit guidance will appear here.' },
];

let msgIdCounter = 0;
const nextId = () => ++msgIdCounter;

/* ------------------------------------------------------- history (local)
   Keyed by the EXISTING Firebase uid (same login as IBC/MyAccount), so every
   account only ever sees its own chats. Logged out -> nothing is loaded or
   saved. No new auth system. */
const RBI_HISTORY_LIMIT = 12;

const rbiHistoryKey = (uid) => `rbi_history_v1:${uid}`;

const loadRbiHistory = (uid) => {
  if (!uid) return [];
  try {
    const raw = window.localStorage.getItem(rbiHistoryKey(uid));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (error) {
    return [];
  }
};

const persistRbiHistory = (uid, list) => {
  if (!uid) return;
  try {
    window.localStorage.setItem(rbiHistoryKey(uid), JSON.stringify(list));
  } catch (error) {
    /* storage unavailable — ignore */
  }
};

function RBIChatbot() {
  const [activeTab, setActiveTab] = useState('rbi');
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const [rbiMessages, setRbiMessages] = useState([]);
  const [webMessages, setWebMessages] = useState([]);
  const [icaiMessages, setIcaiMessages] = useState([]);
  const [rbiLoading, setRbiLoading] = useState(false);
  const [webLoading, setWebLoading] = useState(false);
  const [icaiLoading, setIcaiLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [summaryMessages, setSummaryMessages] = useState([]);
  const [summarySourcesUsed, setSummarySourcesUsed] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFollowUpLoading, setSummaryFollowUpLoading] = useState(false);
  const summaryHistoryRef = useRef([]);
  const summaryContextsRef = useRef([]);
  const summaryChatRef = useRef(null);
  const summaryEndRef = useRef(null);
  const prevSummaryMsgCountRef = useRef(0);

  const [expandedPanel, setExpandedPanel] = useState(null);
  const [apiStatus, setApiStatus] = useState('connecting');
  const [showHelp, setShowHelp] = useState(false);

  /* ------------------------------------------------------------- history */
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [menuForId, setMenuForId] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const currentIdRef = useRef(null);

  /* existing Firebase auth (MyAccount / Navbar) — reused, never extended */
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const prevUidRef = useRef(null);

  const inputRef = useRef(null);
  const allSettersRef = useRef(null);
  const chatRunRef = useRef(0);
  const tabRunRef = useRef({ rbi: 0, web: 0, icai: 0 });
  const navigate = useNavigate();

  const allMessages = useMemo(() => ({ rbi: rbiMessages, web: webMessages, icai: icaiMessages }), [rbiMessages, webMessages, icaiMessages]);
  allSettersRef.current = {
    rbi: { setMessages: setRbiMessages, setLoading: setRbiLoading },
    web: { setMessages: setWebMessages, setLoading: setWebLoading },
    icai: { setMessages: setIcaiMessages, setLoading: setIcaiLoading },
  };
  const allLoading = { rbi: rbiLoading, web: webLoading, icai: icaiLoading };

  const inputMode = (summaryExpanded && summaryGenerated) ? 'summary' : 'common';

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [inputMode]);

  const scrollToSummaryBottom = useCallback((behavior = 'smooth') => {
    if (summaryEndRef.current) {
      summaryEndRef.current.scrollIntoView({ behavior, block: 'end' });
    }
  }, []);

  useEffect(() => {
    if (summaryMessages.length > 0 && summaryExpanded) {
      scrollToSummaryBottom('smooth');
    }
  }, [summaryMessages.length, summaryExpanded, scrollToSummaryBottom]);

  useEffect(() => {
    if (summaryExpanded && summaryChatRef.current) {
      summaryChatRef.current.scrollTop = summaryChatRef.current.scrollHeight;
    }
  }, [summaryExpanded, summaryMessages.length, summaryFollowUpLoading]);

  /* persist history on every change (localStorage — only while logged in) */
  useEffect(() => {
    if (!user) return;
    persistRbiHistory(user.uid, history);
  }, [history, user]);

  /* auto-save the open conversation (created on first response) */
  useEffect(() => {
    if (!user || isProcessing) return;
    const total = allMessages.rbi.length + allMessages.web.length + allMessages.icai.length;
    if (total === 0) return;
    const firstUser =
      allMessages.rbi.find((m) => m.sender === 'user')
      || allMessages.web.find((m) => m.sender === 'user')
      || allMessages.icai.find((m) => m.sender === 'user');
    const title = firstUser ? String(firstUser.text || '').slice(0, 90) : 'Research chat';
    const queries =
      allMessages.rbi.filter((m) => m.sender === 'user').length
      || allMessages.web.filter((m) => m.sender === 'user').length
      || 1;
    let id = currentIdRef.current;
    if (!id) {
      id = `rbi-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      currentIdRef.current = id;
      setCurrentId(id);
    }
    setHistory((prev) => {
      const existing = prev.find((it) => it.id === id);
      const item = {
        id,
        title: (existing && existing.title) || title,
        pinned: existing ? !!existing.pinned : false,
        createdAt: (existing && existing.createdAt) || Date.now(),
        updatedAt: Date.now(),
        queries,
        messages: { rbi: allMessages.rbi, web: allMessages.web, icai: allMessages.icai },
        selectedIds: Array.from(selectedIds),
        summary: {
          expanded: summaryExpanded,
          generated: summaryGenerated,
          messages: summaryMessages,
          sourcesUsed: summarySourcesUsed,
          contexts: summaryContextsRef.current,
          history: summaryHistoryRef.current,
        },
      };
      if (existing) return prev.map((it) => (it.id === id ? item : it));
      return [item, ...prev].slice(0, RBI_HISTORY_LIMIT);
    });
  }, [allMessages, selectedIds, summaryMessages, summaryExpanded, summaryGenerated, summarySourcesUsed, isProcessing, user]);

  /* --------------------------------------------- history item actions (⋮) */
  const closeHistMenu = useCallback(() => {
    setMenuForId(null);
    setMenuPos(null);
  }, []);

  /* fixed positioning so the menu is never clipped by the sidebar */
  const openHistMenu = useCallback((e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const MENU_W = 172;
    const MENU_H = 138;
    const GAP = 6;
    let top = r.bottom + GAP;
    if (top + MENU_H > window.innerHeight - 8) top = Math.max(8, r.top - MENU_H - GAP);
    let left = r.right - MENU_W;
    if (left < 8) left = 8;
    if (left + MENU_W > window.innerWidth - 8) left = window.innerWidth - MENU_W - 8;
    setMenuPos({ top, left });
    setMenuForId((prev) => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    if (!menuForId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeHistMenu(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuForId, closeHistMenu]);

  /* fresh empty workspace (does not delete any history item) */
  const handleNewChat = useCallback(() => {
    chatRunRef.current += 1;
    SOURCES.forEach((src) => { tabRunRef.current[src.id] += 1; });
    currentIdRef.current = null;
    setCurrentId(null);
    setRbiMessages([]);
    setWebMessages([]);
    setIcaiMessages([]);
    setRbiLoading(false);
    setWebLoading(false);
    setIcaiLoading(false);
    setIsProcessing(false);
    setSelectedIds(new Set());
    setSummaryExpanded(false);
    setSummaryGenerated(false);
    setSummaryMessages([]);
    setSummarySourcesUsed([]);
    setSummaryLoading(false);
    setSummaryFollowUpLoading(false);
    summaryContextsRef.current = [];
    summaryHistoryRef.current = [];
    setExpandedPanel(null);
    setInputValue('');
    setHistoryOpen(false);
    setRenamingId(null);
    closeHistMenu();
  }, [closeHistMenu]);

  /* ------------------------------------------------------------- health */
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/ibc/health`);
        if (!cancelled) setApiStatus(response.ok ? 'connected' : 'error');
      } catch {
        if (!cancelled) setApiStatus('error');
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const handleRefresh = useCallback(() => {
    setApiStatus('connecting');
    fetch(`${API_BASE_URL}/api/ibc/health`)
      .then((r) => setApiStatus(r.ok ? 'connected' : 'error'))
      .catch(() => setApiStatus('error'));
    handleNewChat();
  }, [handleNewChat]);

  /* --------------------------------------------------- auth (existing Firebase login)
     Same behaviour as IBC: logged out -> login prompt in the sidebar, each
     uid loads only its own history, logout/account switch wipes whatever the
     previous account had on screen. */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const uid = user ? user.uid : null;
    if (prevUidRef.current && prevUidRef.current !== uid) {
      /* logout or account switch — clear the previous user's chat + history */
      chatRunRef.current += 1;
      currentIdRef.current = null;
      setCurrentId(null);
      setRbiMessages([]);
      setWebMessages([]);
      setIcaiMessages([]);
      setRbiLoading(false);
      setWebLoading(false);
      setIcaiLoading(false);
      setIsProcessing(false);
      setSelectedIds(new Set());
      setSummaryExpanded(false);
      setSummaryGenerated(false);
      setSummaryMessages([]);
      setSummarySourcesUsed([]);
      setSummaryLoading(false);
      setSummaryFollowUpLoading(false);
      summaryContextsRef.current = [];
      summaryHistoryRef.current = [];
      setExpandedPanel(null);
      setInputValue('');
      setHistory([]);
      setRenamingId(null);
      setRenameValue('');
      setDeleteId(null);
      closeHistMenu();
    }
    prevUidRef.current = uid;
    setHistory(uid ? loadRbiHistory(uid) : []);
  }, [user, closeHistMenu]);

  /* reopen a stored conversation without calling any API */
  const openRbiHistory = useCallback((item) => {
    if (!item) return;
    chatRunRef.current += 1;
    currentIdRef.current = item.id;
    setCurrentId(item.id);
    const m = item.messages || {};
    setRbiMessages(m.rbi || []);
    setWebMessages(m.web || []);
    setIcaiMessages(m.icai || []);
    setRbiLoading(false);
    setWebLoading(false);
    setIcaiLoading(false);
    setIsProcessing(false);
    setSelectedIds(new Set(item.selectedIds || []));
    const s = item.summary || {};
    setSummaryExpanded(!!s.expanded);
    setSummaryGenerated(!!s.generated);
    setSummaryMessages(s.messages || []);
    setSummarySourcesUsed(s.sourcesUsed || []);
    setSummaryLoading(false);
    setSummaryFollowUpLoading(false);
    summaryContextsRef.current = s.contexts || [];
    summaryHistoryRef.current = s.history || [];
    setExpandedPanel(null);
    setInputValue('');
    setHistoryOpen(false);
    setRenamingId(null);
    closeHistMenu();
  }, [closeHistMenu]);

  const togglePinHistory = useCallback((id) => {
    setHistory((prev) => prev.map((it) => (it.id === id ? { ...it, pinned: !it.pinned } : it)));
    closeHistMenu();
  }, [closeHistMenu]);

  const startRenameHistory = useCallback((item) => {
    if (!item) return;
    setRenamingId(item.id);
    setRenameValue(item.title || '');
    closeHistMenu();
  }, [closeHistMenu]);

  const commitRenameHistory = useCallback(() => {
    const title = renameValue.trim();
    if (renamingId && title) {
      setHistory((prev) => prev.map((it) => (it.id === renamingId ? { ...it, title } : it)));
    }
    setRenamingId(null);
    setRenameValue('');
  }, [renamingId, renameValue]);

  const cancelRenameHistory = useCallback(() => {
    setRenamingId(null);
    setRenameValue('');
  }, []);

  const requestDeleteHistory = useCallback((id) => {
    setDeleteId(id);
    closeHistMenu();
  }, [closeHistMenu]);

  const confirmDeleteHistory = useCallback(() => {
    const id = deleteId;
    setDeleteId(null);
    if (!id) return;
    setHistory((prev) => prev.filter((it) => it.id !== id));
    if (currentIdRef.current === id) handleNewChat();
  }, [deleteId, handleNewChat]);

  const handleSendCommon = useCallback(async (queryOverride) => {
    const query = (queryOverride || inputValue).trim();
    if (!query || isProcessing) return;

    const setters = allSettersRef.current;
    if (!setters) return;

    setInputValue('');
    if (inputRef.current) inputRef.current.focus(); // keep cursor ready for the next message
    setIsProcessing(true);
    setSelectedIds(new Set());
    const runId = (chatRunRef.current += 1);

    const userMsgs = {};
    SOURCES.forEach((src) => {
      tabRunRef.current[src.id] += 1;
      const userMsg = { id: nextId(), sender: 'user', text: query };
      userMsgs[src.id] = userMsg;
      setters[src.id].setMessages((prev) => [...prev, userMsg]);
      setters[src.id].setLoading(true);
    });

    const historyPayload = [];

    const fetchSource = async (src) => {
      const tabRun = tabRunRef.current[src.id];
      try {
        const res = await fetch(`${API_BASE_URL}${src.endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question: query, history: historyPayload }),
        });
        const data = res.ok ? await res.json() : null;

        let botMsg = { id: nextId(), sender: 'bot', text: '', sources: [], isError: false };

        if (src.id === 'rbi') {
          const ok = data && data.answer && !data.answer.includes("couldn't verify this");
          botMsg.text = ok
            ? data.answer.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '')
            : "I couldn't verify this from the available RBI information.";
          botMsg.sources = ok ? (data.sources || []) : [];
          botMsg.isError = !ok;
        } else if (src.id === 'web') {
          if (data && data.answer && !isGenuineError(data.answer)) {
            botMsg.text = data.answer.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '');
            botMsg.sources = sanitizeSources(data.web_sources || []);
            botMsg.isError = false;
          } else if (data && data.web_sources && data.web_sources.length > 0) {
            botMsg.text = data.answer || 'Here are relevant web results:';
            botMsg.sources = sanitizeSources(data.web_sources);
            botMsg.isError = false;
          } else {
            botMsg.text = data?.answer || 'No web results found. Please try rephrasing your question.';
            botMsg.isError = true;
          }
        } else {
          const ok = data && data.answer && !data.answer.includes("couldn't verify this");
          botMsg.text = ok
            ? data.answer.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '')
            : "I couldn't verify this from the available ICAI information.";
          botMsg.sources = ok ? (data.sources || []) : [];
          botMsg.isError = !ok;
        }

        if (tabRun !== tabRunRef.current[src.id]) return;
        setters[src.id].setMessages((prev) => [...prev, botMsg]);
      } catch {
        if (tabRun !== tabRunRef.current[src.id]) return;
        setters[src.id].setMessages((prev) => [
          ...prev,
          { id: nextId(), sender: 'bot', text: 'Unable to get a response. Please try again.', sources: [], isError: true },
        ]);
      } finally {
        if (tabRun === tabRunRef.current[src.id]) setters[src.id].setLoading(false);
      }
    };

    await Promise.allSettled(SOURCES.map(fetchSource));
    if (runId === chatRunRef.current) setIsProcessing(false);
  }, [inputValue, isProcessing]);

  const handleSendSummaryFollowUp = useCallback(async () => {
    const followUpText = inputValue.trim();
    if (!followUpText || summaryFollowUpLoading || summaryMessages.length === 0) return;

    setInputValue('');
    if (inputRef.current) inputRef.current.focus(); // keep cursor ready for the next message
    setSummaryFollowUpLoading(true);
    const runId = chatRunRef.current;

    const userMsg = { id: nextId(), sender: 'user', text: followUpText };
    setSummaryMessages((prev) => [...prev, userMsg]);

    const selectedContexts = summaryContextsRef.current;
    const updatedHistory = [
      ...summaryHistoryRef.current,
      { role: 'user', content: followUpText },
    ];

    try {
      const res = await fetch(`${API_BASE_URL}/api/combined-followup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selected_contexts: selectedContexts,
          follow_up: followUpText,
          history: updatedHistory,
        }),
      });
      const data = res.ok ? await res.json() : null;
      if (runId !== chatRunRef.current) return;

      if (data && data.summary && !data.error) {
        const cleaned = data.summary.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '');
        const botMsg = { id: nextId(), sender: 'assistant', text: cleaned };
        setSummaryMessages((prev) => [...prev, botMsg]);
        summaryHistoryRef.current = [
          ...updatedHistory,
          { role: 'assistant', content: cleaned },
        ];
      } else {
        const errMsg = { id: nextId(), sender: 'assistant', text: data?.summary || 'Error: No response.' };
        setSummaryMessages((prev) => [...prev, errMsg]);
      }
    } catch {
      if (runId !== chatRunRef.current) return;
      const errMsg = { id: nextId(), sender: 'assistant', text: 'Error: Failed to get follow-up response.' };
      setSummaryMessages((prev) => [...prev, errMsg]);
    } finally {
      if (runId === chatRunRef.current) setSummaryFollowUpLoading(false);
    }
  }, [inputValue, summaryFollowUpLoading, summaryMessages.length]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputMode === 'summary') {
        handleSendSummaryFollowUp();
      } else {
        handleSendCommon();
      }
    }
  };

  const handleChipClick = (chip) => {
    handleSendCommon(chip);
  };

  const handleRetry = useCallback(async (sourceId, msgId) => {
    const msgs = allMessages[sourceId];
    if (!msgs || msgs.length === 0) return;
    const target = msgs.find((m) => m.id === msgId);
    if (!target || !target.isError || target.retrying) return;
    const lastUserMsg = [...msgs].reverse().find((m) => m.sender === 'user');
    if (!lastUserMsg) return;

    const setters = allSettersRef.current;
    const tabRun = (tabRunRef.current[sourceId] += 1);
    const src = SOURCES.find((s) => s.id === sourceId);

    // same div -> in-place loading state (no new message, no extra space)
    setters[sourceId].setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, retrying: true } : m)));

    try {
      const res = await fetch(`${API_BASE_URL}${src.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: lastUserMsg.text, history: [] }),
      });
      const data = res.ok ? await res.json() : null;

      let botMsg = { id: msgId, sender: 'bot', text: '', sources: [], isError: false };

      if (sourceId === 'rbi') {
        const ok = data && data.answer && !data.answer.includes("couldn't verify this");
        botMsg.text = ok
          ? data.answer.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '')
          : "I couldn't verify this from the available RBI information.";
        botMsg.sources = ok ? (data.sources || []) : [];
        botMsg.isError = !ok;
      } else if (sourceId === 'web') {
        if (data && data.answer && !isGenuineError(data.answer)) {
          botMsg.text = data.answer.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '');
          botMsg.sources = sanitizeSources(data.web_sources || []);
          botMsg.isError = false;
        } else if (data && data.web_sources && data.web_sources.length > 0) {
          botMsg.text = data.answer || 'Here are relevant web results:';
          botMsg.sources = sanitizeSources(data.web_sources);
          botMsg.isError = false;
        } else {
          botMsg.text = data?.answer || 'No web results found. Please try rephrasing your question.';
          botMsg.isError = true;
        }
      } else {
        const ok = data && data.answer && !data.answer.includes("couldn't verify this");
        botMsg.text = ok
          ? data.answer.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '')
          : "I couldn't verify this from the available ICAI information.";
        botMsg.sources = ok ? (data.sources || []) : [];
        botMsg.isError = !ok;
      }

      // replace the SAME message div (same id -> React updates in place)
      if (tabRun !== tabRunRef.current[sourceId]) return;
      setters[sourceId].setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...botMsg, retrying: false } : m)));
    } catch {
      if (tabRun !== tabRunRef.current[sourceId]) return;
      setters[sourceId].setMessages((prev) => prev.map((m) => (m.id === msgId
        ? { id: msgId, sender: 'bot', text: 'Retry failed. Please try again later.', sources: [], isError: true, retrying: false }
        : m)));
    } finally {
      // always release the in-place loading (even if superseded by a newer run)
      setters[sourceId].setMessages((prev) => prev.map((m) => (m.id === msgId && m.retrying ? { ...m, retrying: false } : m)));
    }
  }, [allMessages]);

  const handleCopyResponse = useCallback((text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  }, []);

  const handleToggleSelect = useCallback((msgId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSummaryExpanded(false);
    setSummaryGenerated(false);
    setSummaryMessages([]);
    setSummarySourcesUsed([]);
    summaryHistoryRef.current = [];
    summaryContextsRef.current = [];
    if (expandedPanel === 'summary') setExpandedPanel(null);
  }, [expandedPanel]);

  const getSelectedContext = useCallback(() => {
    const contexts = [];
    SOURCES.forEach((src) => {
      const msgs = allMessages[src.id];
      const botMsgs = msgs.filter((m) => m.sender === 'bot' && !m.isError && selectedIds.has(m.id));
      botMsgs.forEach((m) => {
        contexts.push({ source: src.title, sourceId: src.id, text: m.text, sources: m.sources });
      });
    });
    return contexts;
  }, [allMessages, selectedIds]);

  const handleGenerateSummary = useCallback(async () => {
    const ctx = getSelectedContext();
    if (ctx.length === 0) return;

    setSummaryExpanded(true);
    setSummaryGenerated(true);
    setSummaryLoading(true);
    setSummaryMessages([]);
    summaryHistoryRef.current = [];

    const selectedContexts = ctx.map((c) => ({
      source: c.source.replace(' Research', ''),
      content: c.text,
    }));

    summaryContextsRef.current = selectedContexts;
    const runId = chatRunRef.current;

    try {
      const res = await fetch(`${API_BASE_URL}/api/combined-followup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selected_contexts: selectedContexts,
          follow_up: 'Combine these findings into a single coherent summary.',
          history: [],
        }),
      });
      const data = res.ok ? await res.json() : null;
      if (runId !== chatRunRef.current) return;

      if (data && data.summary && !data.error) {
        const cleaned = data.summary.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '');
        const botMsg = { id: nextId(), sender: 'assistant', text: cleaned };
        setSummaryMessages([botMsg]);
        setSummarySourcesUsed(data.sources_used || []);
        summaryHistoryRef.current = [{ role: 'assistant', content: cleaned }];
      } else {
        const errMsg = { id: nextId(), sender: 'assistant', text: data?.summary || 'Error: Could not generate combined summary.' };
        setSummaryMessages([errMsg]);
      }
    } catch {
      if (runId !== chatRunRef.current) return;
      const errMsg = { id: nextId(), sender: 'assistant', text: 'Error: Failed to connect to combined summary service.' };
      setSummaryMessages([errMsg]);
    } finally {
      if (runId === chatRunRef.current) setSummaryLoading(false);
    }
  }, [getSelectedContext]);

  const handlePanelExpand = useCallback((panelId) => {
    setExpandedPanel((prev) => (prev === panelId ? null : panelId));
  }, []);

  const selectedCount = selectedIds.size;

  const placeholder = inputMode === 'summary'
    ? 'Ask a follow-up about this combined summary...'
    : 'Ask anything about RBI, Web Search, or ICAI Research...';

  const inputHint = inputMode === 'summary'
    ? 'Answering from Combined Summary'
    : 'Sends to RBI + Web + ICAI simultaneously. Shift+Enter for new line.';

  /* history sidebar: pinned first, then recent (existing order kept) */
  const pinnedItems = history.filter((it) => it.pinned);
  const recentItems = history.filter((it) => !it.pinned);
  const menuIsPinned = menuForId ? !!(history.find((it) => it.id === menuForId) || {}).pinned : false;
  const deleteItem = deleteId ? history.find((it) => it.id === deleteId) : null;

  const renderHistoryItem = (it) => {
    const renaming = renamingId === it.id;
    const msgCount = it.queries || 1;
    return (
      <div
        key={it.id}
        className={`uws-sidebar-item ${currentId === it.id ? 'active' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => { if (!renaming) openRbiHistory(it); }}
        onKeyDown={(e) => {
          if (!renaming && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openRbiHistory(it); }
        }}
        title={it.title}
      >
        {renaming ? (
          <div className="uws-sidebar-rename" onClick={(e) => e.stopPropagation()}>
            <input
              className="uws-sidebar-rename-input"
              value={renameValue}
              autoFocus
              onChange={(e) => setRenameValue(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitRenameHistory(); }
                else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelRenameHistory(); }
              }}
              onBlur={commitRenameHistory}
              aria-label="Chat name"
            />
            <button
              type="button"
              className="uws-sidebar-rename-save"
              onMouseDown={(e) => e.preventDefault()}
              onClick={commitRenameHistory}
              title="Save name"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><polyline points="20 6 9 17 4 12" /></svg>
            </button>
          </div>
        ) : (
          <>
            <span className="uws-sidebar-item-title">
              {it.pinned && (
                <svg className="uws-sidebar-pin-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 17v5" />
                  <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
                </svg>
              )}
              <span className="uws-sidebar-item-text">{it.title}</span>
            </span>
            <span className="uws-sidebar-item-meta">
              {msgCount} message{msgCount === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              className="uws-sidebar-dots"
              title="Chat options"
              aria-label={`Options for ${it.title}`}
              onClick={(e) => openHistMenu(e, it.id)}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true">
                <circle cx="12" cy="5" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="12" cy="19" r="1.9" />
              </svg>
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="uws-page">
      <div className="uws-container">
        <header className="uws-topbar">
          <div className="uws-topbar-left">
            <button
              type="button"
              className="uws-topbar-btn"
              onClick={() => setHistoryOpen((o) => !o)}
              aria-label={historyOpen ? 'Hide chat history' : 'Show chat history'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2"/><line x1="9" y1="4" x2="9" y2="20"/></svg>
              {historyOpen ? 'Hide history' : 'History'}
            </button>
            <div className="uws-logo">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              <span>DataBoltaHai</span>
            </div>
            <h1 className="uws-title">AI Research Workspace</h1>
          </div>
          <div className="uws-topbar-right">
            <div className={`uws-connection ${apiStatus}`}>
              <span className="uws-connection-dot" />
              {apiStatus === 'connected' ? 'Connected' : apiStatus === 'error' ? 'Disconnected' : 'Connecting'}
            </div>
            <button type="button" className="uws-topbar-btn" onClick={() => setShowHelp(true)} aria-label="Help">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              Help
            </button>
            <button type="button" className="uws-topbar-btn" onClick={handleRefresh} aria-label="Refresh">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><line x1="20" y1="5" x2="20" y2="11"/><path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              Refresh
            </button>
            <span className="uws-badge">RBI + Web + ICAI</span>
          </div>
        </header>

        {/* ── History sidebar (same functionality as the IBC chatbot) ── */}
        {historyOpen && <div className="uws-sidebar-backdrop" onClick={() => setHistoryOpen(false)} />}
        <aside className={`uws-sidebar ${historyOpen ? 'open' : ''}`} aria-hidden={!historyOpen}>
          <div className="uws-sidebar-head">
            <button type="button" className="uws-sidebar-new" onClick={handleNewChat}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Chat
            </button>
            <button type="button" className="uws-sidebar-close" onClick={() => setHistoryOpen(false)} aria-label="Close history sidebar">&times;</button>
          </div>
          <div className="uws-sidebar-label">History</div>
          <div className="uws-sidebar-list">
            {!authReady ? (
              <div className="uws-sidebar-empty">Loading…</div>
            ) : !user ? (
              <div className="uws-sidebar-login">
                <p className="uws-sidebar-login-text">Log in to view your chat history</p>
                <button
                  type="button"
                  className="uws-sidebar-login-btn"
                  onClick={() => navigate('/my-account')}
                >
                  Login
                </button>
              </div>
            ) : history.length === 0 ? (
              <div className="uws-sidebar-empty">Your conversations will appear here after your first chat.</div>
            ) : (
              <>
                {pinnedItems.length > 0 && <div className="uws-sidebar-group">Pinned</div>}
                {pinnedItems.map(renderHistoryItem)}
                {recentItems.length > 0 && <div className="uws-sidebar-group">Recent</div>}
                {recentItems.map(renderHistoryItem)}
              </>
            )}
          </div>
        </aside>

        <div className="uws-mobile-tabs">
          {SOURCES.map((src) => (
            <button
              key={src.id}
              className={`uws-mobile-tab ${activeTab === src.id ? 'active' : ''}`}
              onClick={() => setActiveTab(src.id)}
              style={activeTab === src.id ? { '--tab-accent': src.accent } : {}}
            >
              <span>{src.icon}</span> {src.title.replace(' Research', '')}
            </button>
          ))}
        </div>

        <div className="uws-workspace">
          {summaryExpanded && summaryGenerated ? (
            <div className="uws-summary-expanded">
              <div className="uws-summary-expanded-header">
                <div className="uws-summary-expanded-header-left">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  <span className="uws-summary-expanded-title">Combined Summary</span>
                  <span className="uws-summary-sources-badge">
                    {summarySourcesUsed.length > 0
                      ? summarySourcesUsed.join(' + ')
                      : getSelectedContext().map((c) => c.source.replace(' Research', '')).join(' + ')}
                  </span>
                </div>
                <div className="uws-summary-expanded-header-right">
                  <button className="uws-btn-icon" onClick={() => { navigator.clipboard.writeText(summaryMessages.map(m => m.text).join('\n\n')).catch(() => {}); }} title="Copy summary">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                    </svg>
                  </button>
                  <button className="uws-btn-icon" onClick={handleGenerateSummary} title="Regenerate">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
                    </svg>
                  </button>
                  <button className="uws-btn-icon" onClick={() => { setSummaryExpanded(false); }} title="Collapse">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/>
                    </svg>
                  </button>
                  <button className="uws-btn-icon uws-btn-icon-danger" onClick={handleClearSelection} title="Close summary">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="uws-summary-expanded-body" ref={summaryChatRef}>
                {summaryLoading ? (
                  <div className="uws-summary-loading">
                    <div className="uws-typing"><span/><span/><span/></div>
                    <span>Generating combined summary...</span>
                  </div>
                ) : (
                  <div className="uws-summary-messages">
                    {summaryMessages.map((msg) => (
                      <div key={msg.id} className={`uws-summary-msg-row ${msg.sender}`}>
                        <div className={`uws-summary-msg ${msg.sender}`}>
                          {msg.sender === 'user' ? (
                            <div className="uws-summary-msg-user">{msg.text}</div>
                          ) : (
                            <div className="uws-summary-msg-assistant">
                              <div className="uws-summary-msg-avatar">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                                </svg>
                              </div>
                              <div className="uws-summary-msg-content">
                                <div className="uws-summary-markdown">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                                </div>
                                <div className="uws-summary-msg-actions">
                                  <button className="uws-summary-action-btn" onClick={() => { navigator.clipboard.writeText(msg.text).catch(() => {}); }} title="Copy">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {summaryFollowUpLoading && (
                      <div className="uws-summary-msg-row assistant">
                        <div className="uws-summary-msg assistant">
                          <div className="uws-summary-msg-assistant">
                            <div className="uws-summary-msg-avatar">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                              </svg>
                            </div>
                            <div className="uws-summary-msg-content">
                              <div className="uws-typing"><span/><span/><span/></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={summaryEndRef} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="uws-grid">
              {SOURCES.map((src) => (
                <div
                  key={src.id}
                  className={`uws-grid-cell ${activeTab === src.id ? 'mobile-active' : ''} ${expandedPanel === src.id ? 'panel-expanded' : ''} ${expandedPanel && expandedPanel !== src.id && expandedPanel !== 'summary' ? 'panel-compact' : ''}`}
                >
                  <SourceSection
                    source={src}
                    messages={allMessages[src.id]}
                    isLoading={allLoading[src.id]}
                    selectedIds={selectedIds}
                    onToggleSelect={handleToggleSelect}
                    onRetry={handleRetry}
                    onCopy={handleCopyResponse}
                    onChipClick={handleChipClick}
                    isProcessing={isProcessing}
                    isExpanded={expandedPanel === src.id}
                    onExpand={() => handlePanelExpand(src.id)}
                  />
                </div>
              ))}
            </div>
          )}

          {selectedCount > 0 && !summaryExpanded && (
            <div className="uws-selection-bar">
              <span className="uws-selection-count">{selectedCount} response{selectedCount > 1 ? 's' : ''} selected</span>
              <div className="uws-selection-actions">
                <button className="uws-btn uws-btn-generate" onClick={handleGenerateSummary}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  Generate Combined Summary
                </button>
                <button className="uws-btn uws-btn-clear" onClick={handleClearSelection}>Clear</button>
              </div>
            </div>
          )}

          {summaryGenerated && !summaryExpanded && (
            <div className="uws-summary-minimized">
              <div className="uws-summary-minimized-left">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                <span className="uws-summary-minimized-title">Combined Summary</span>
                <span className="uws-summary-minimized-badge">
                  {summarySourcesUsed.length > 0
                    ? summarySourcesUsed.join(' + ')
                    : 'RBI + ICAI'}
                </span>
              </div>
              <div className="uws-summary-minimized-right">
                <button
                  className="uws-summary-expand-btn"
                  onClick={() => setSummaryExpanded(true)}
                  title="Expand summary"
                >
                  Expand
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
                  </svg>
                </button>
                <button
                  className="uws-summary-close-btn"
                  onClick={handleClearSelection}
                  title="Close and delete summary"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={`uws-common-input ${inputMode === 'summary' ? 'uws-common-input--summary' : ''}`}>
          <div className="uws-input-wrapper">
            {inputMode === 'summary' && (
              <span className="uws-input-mode-badge">Summary</span>
            )}
            <textarea
              ref={inputRef}
              className="uws-textarea"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={1}
            />
            <button
              className="uws-send-btn"
              onClick={() => {
                if (inputMode === 'summary') {
                  handleSendSummaryFollowUp();
                } else {
                  handleSendCommon();
                }
              }}
              disabled={!inputValue.trim() || isProcessing || summaryFollowUpLoading}
              aria-label={inputMode === 'summary' ? 'Send follow-up' : 'Send to all sources'}
            >
              {(isProcessing || summaryFollowUpLoading) ? (
                <div className="uws-typing uws-typing-btn"><span/><span/><span/></div>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              )}
            </button>
          </div>
          <div className="uws-input-hint">
            {inputHint}
          </div>
        </div>
      </div>

      {/* ── history item menu (Rename / Pin / Delete) ── */}
      {menuForId && <div className="uws-hist-menu-overlay" onMouseDown={closeHistMenu} />}
      {menuForId && menuPos && (
        <div className="uws-hist-menu" style={{ top: menuPos.top, left: menuPos.left }} role="menu">
          <button type="button" className="uws-hist-menu-item" role="menuitem" onClick={() => startRenameHistory(history.find((it) => it.id === menuForId))}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
              <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
            Rename
          </button>
          <button type="button" className="uws-hist-menu-item" role="menuitem" onClick={() => togglePinHistory(menuForId)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
              <path d="M12 17v5" />
              <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
            </svg>
            {menuIsPinned ? 'Unpin chat' : 'Pin chat'}
          </button>
          <div className="uws-hist-menu-sep" />
          <button type="button" className="uws-hist-menu-item danger" role="menuitem" onClick={() => requestDeleteHistory(menuForId)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden="true">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            Delete
          </button>
        </div>
      )}

      {/* ── delete confirmation ── */}
      {deleteId && (
        <div className="uws-confirm-overlay" onMouseDown={() => setDeleteId(null)}>
          <div className="uws-confirm-card" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="uws-confirm-title">Delete this chat?</div>
            <div className="uws-confirm-text" title={(deleteItem || {}).title}>{(deleteItem || {}).title}</div>
            <div className="uws-confirm-actions">
              <button type="button" className="uws-confirm-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button type="button" className="uws-confirm-ok" onClick={confirmDeleteHistory}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Help Modal (IBC-style) ── */}
      {showHelp && (
        <div className="uws-help-overlay" onClick={() => setShowHelp(false)}>
          <div className="uws-help-card" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="uws-help-close" onClick={() => setShowHelp(false)} aria-label="Close">&times;</button>
            <h3>How to Use</h3>
            <div className="uws-help-steps">
              <div className="uws-help-step"><div className="uws-help-step-num">1</div><div className="uws-help-step-text"><strong>Ask once</strong> — type your question in the bottom input; it goes to RBI, Web and ICAI at the same time. English, Hindi and Hinglish all work.</div></div>
              <div className="uws-help-step"><div className="uws-help-step-num">2</div><div className="uws-help-step-text"><strong>RBI Research</strong> — answers grounded in official RBI Master Directions and circulars.</div></div>
              <div className="uws-help-step"><div className="uws-help-step-num">3</div><div className="uws-help-step-text"><strong>Web Research</strong> — live web research with cited sources, for RBI, banking/financial and ICAI topics.</div></div>
              <div className="uws-help-step"><div className="uws-help-step-num">4</div><div className="uws-help-step-text"><strong>ICAI Research</strong> — audit guidance, accounting standards and bank-audit procedures.</div></div>
              <div className="uws-help-step"><div className="uws-help-step-num">5</div><div className="uws-help-step-text"><strong>Combined Summary</strong> — add responses with Select, open the summary, then type in the bottom input and press <strong>Send</strong> to generate it. Ask follow-ups in the same input.</div></div>
            </div>
            <p className="uws-help-note">Answers are grounded in official sources — a research aid, not legal or financial advice.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default RBIChatbot;
